import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, message, Table, Tooltip, Row, Col } from 'antd';
import {
  TeamBadge,
  CustomPagination,
  PageHeaderActions,
  CommonDrawer,
  CustomShipmentText,
  CustomParagraph,
} from '../../../components';
import userIcon from '../../../assets/images/greyIcons/user_gray.svg';
import { UserActionsMenu, UsersHeaderMenu } from './components';
import { ActivityFeedPanel } from './ActivityFeed';
import {
  isExecutionLegEvent,
  isCompanyAlertNotificationEvent,
  isShipmentNotificationEvent,
} from './ActivityFeed/utils/filterEvents/filterEvents';
import {
  useToken,
  getPermissions,
  getInitials,
  capitalize,
} from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import { AddUsers } from '../../../apis/onboarding';
import { ResendInvite, DeletePendingInviteUser } from '../../../apis/settings';
import { CustomDeleteModal } from '../../Modals';
import SettingsInviteUserForm from './SettingsInviteUserForm';
import InviteUserModal from './InviteUserModal';
import { useSelector, useDispatch } from 'react-redux';
import { setShowDisabledUsers } from '../../../redux-toolkit/slice/SettingsUsersSlice';
import {
  useViewAsUser,
  useUnAuthorize,
  useTableScroll,
  useUserDateTimeFormat,
  useBreakpoints,
} from '@/hooks';
import { truncateByLength } from '../../../common/helper';
import { SuperAdmin } from '@/assets/icons/icons';
import theme from '@/assets/theme';
import useFetchUsers from './hooks';

const SettingsUserTable = ({ title }) => {
  const { token, decodedUserToken } = useToken();
  const { handleViewAsUser } = useViewAsUser();
  const { formatDateTime } = useUserDateTimeFormat();
  const { unauthorize } = useUnAuthorize();
  const { isMobile } = useBreakpoints();
  const [form] = Form.useForm();
  const { showSpin, hideSpin } = useSpin();
  const [userID, setUserID] = useState('');
  const [deleteData, setDeleteData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [search, setSearch] = useState('');
  const allTeams = useSelector((state) => state.teams.list);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [is_disable, setIsDisable] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const main_menu = useSelector((state) => state.menu.items);
  const UserPermissions = getPermissions(main_menu, 'Settings', 'Users');
  const [deleteType, setDeleteType] = useState(null);
  const isChecked = useSelector(
    (state) => state.settingsUsers.showDisabledUsers
  );
  const dispatch = useDispatch();
  const tableScroll = useTableScroll();
  // View as user: company owners only (uat's own scope for this), never on
  // the default admin row, never during a role preview.
  const isCompanyOwner = decodedUserToken()?.is_company_owner === true;
  const previewMode = decodedUserToken()?.preview_mode;
  const canViewAsUserBase =
    !isMobile && isCompanyOwner && previewMode?.toLowerCase() !== 'role';

  // Activity feed/export are admin-only per the User activity PRD — any
  // admin, not just the company owner.
  const canSeeUserActivity = isCompanyOwner;

  const handleToggle = (e) => {
    const val = e.target.checked;
    dispatch(setShowDisabledUsers(val));
    setPage(1);
  };

  const { userList, totalPages, maxPageSize, fetchUsers } = useFetchUsers();

  useEffect(() => {
    fetchUsers(isChecked, page, search);
  }, [page, search, isChecked, fetchUsers]);

  const handleEdit = (record) => {
    setDeleteType(2);
    setIsDisable(false);
    setDeleteData(record);
    setShowDeleteModal(true);
  };

  const handleEditUser = (record) => {
    setIsDisable(false);
    let userId = record?.id ? record.id : '';
    setDeleteData(record);
    setUserID(userId);
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setUserID(null);
    setShowDrawer(false);
    form.resetFields();
    fetchUsers(isChecked, page, search);
  };

  const handleDelete = (record, type) => {
    setDeleteType(type);
    setIsDisable(true);
    setDeleteData(record);
    setShowDeleteModal(true);
  };

  // Restores the drawer state immediately when returning via browser back,
  // preventing a flash of the closed panel before the effect runs.
  const navigate = useNavigate();
  const location = useLocation();
  const initialActivityRestore = location.state?.__zl_activity_restore ?? null;
  const [activityPanel, setActivityPanel] = useState(() => ({
    open: Boolean(initialActivityRestore),
    user: initialActivityRestore ? initialActivityRestore.singleUser : null,
    restoreState: initialActivityRestore,
  }));
  const handleOpenActivity = (record) => {
    setActivityPanel({
      open: true,
      user: record ? { id: record.id, name: record.name } : null,
      restoreState: null,
    });
  };
  const handleCloseActivity = () => {
    setActivityPanel((prev) => ({ ...prev, open: false, restoreState: null }));
  };

  // landing back on that stamped entry so the panel can restore from it.
  const restoreConsumedKeyRef = useRef(null);
  useEffect(() => {
    const restore = location.state?.__zl_activity_restore;
    if (!restore) return;
    if (restoreConsumedKeyRef.current === location.key) return;
    restoreConsumedKeyRef.current = location.key;
    setActivityPanel({
      open: true,
      user: restore.singleUser,
      restoreState: restore,
    });
    // One-shot: strip it from history so revisiting this entry later (e.g.
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.state]);

  const handleActivityEventClick = (event, snapshot) => {
    // A company-wide alert has no shipment: it opens in Settings → Notifications.
    const isCompanyAlert = isCompanyAlertNotificationEvent(event);
    // An execution leg has no shipment_id: its drawer opens by the leg's own id.
    const isExecutionLeg = isExecutionLegEvent(event);
    const targetId = isExecutionLeg
      ? event.execution_leg_id
      : event.shipment_id;
    if (!targetId && !isCompanyAlert) return;
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: { __zl_activity_restore: snapshot } }
    );
    if (isCompanyAlert) {
      // Router state, not the URL, so the id never leaks into shared links.
      navigate('/settings/notification', {
        state: { __zl_open_notification_id: event.coc_id },
      });
      return;
    }
    // Its coc_id is the notification's id, not a CoC comment's.
    const isShipmentNotification = isShipmentNotificationEvent(event);
    const params = new URLSearchParams();
    params.set('shipment_id', targetId);
    params.set('leg_type', isExecutionLeg ? 'EL' : 'CL');
    if (event.coc_id && !isShipmentNotification) {
      params.set('highlight', event.coc_id);
    }
    // Keep this in router state, not the URL, so copied/shared links never expose
    // the "Back to User Activity"
    navigate(`/shipment?${params.toString()}`, {
      state: {
        __zl_from_activity: true,
        ...(isShipmentNotification && {
          __zl_open_shipment_notifications: true,
          __zl_open_shipment_notification: event.coc_id,
        }),
      },
    });
  };

  const disableUser = (isActive) => {
    setShowDeleteModal(false);
    if (deleteData && deleteData.id) {
      showSpin();
      let model = { ...deleteData, is_active: isActive };
      model.designation = model?.designation?.id;
      AddUsers(token, model, false)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              message.success(data?.data?.message);
              fetchUsers(isChecked, page, search);
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  const handleConfirmDelete = () => {
    if (deleteType === 0) {
      handlePendingDelete();
    } else {
      disableUser(deleteType !== 1);
    }
  };
  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handlePageSizeChange = (newPageSize) => {
    if (!newPageSize || newPageSize < 1) return;
    if (newPageSize > 200) newPageSize = 200;

    setPageSize(newPageSize);
    setPage(1);
    fetchUsers(isChecked, 1, search);
  };

  const handlePageChange = (newPage) => {
    setPage(() => {
      const validPage = newPage > 0 ? newPage : 1;
      fetchUsers(isChecked, validPage, search);
      return validPage;
    });
  };

  const handlePendingDelete = () => {
    setShowDeleteModal(false);
    if (deleteData && deleteData.id) {
      showSpin();
      DeletePendingInviteUser(token, deleteData.id)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              message.success(data?.data?.message);
              fetchUsers(isChecked, page, search);
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  const columns = [
    {
      title: '',
      key: 'avatar',
      width: 48,
      render: (_, record) => {
        const isInactive = !record?.is_active && !record?.is_pending;
        // Only an active user's own badge_color is applied inline; every other
        // state is a themed class (see Users.scss).
        const badgeColor =
          record?.is_active && !record?.is_pending
            ? record?.badge_color
            : undefined;
        const avatarState = record?.is_pending
          ? 'initials-avatar--pending'
          : isInactive
            ? 'initials-avatar--inactive'
            : !badgeColor
              ? 'initials-avatar--default'
              : '';

        return (
          <div
            className={`initials-avatar ${avatarState}`.trim()}
            // background comes from the user's badge_color
            // eslint-disable-next-line react/forbid-dom-props
            style={badgeColor ? { backgroundColor: badgeColor } : undefined}
          >
            {record?.is_pending ? (
              <img
                src={userIcon}
                alt='User'
                className='settings-users-pending-icon'
              />
            ) : (
              getInitials(record?.name)
            )}
          </div>
        );
      },
    },

    {
      title: 'Name / title',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <>
          <span className='settings-users-name'>
            {record?.name ? capitalize(record?.name) : '--'}
            {record?.id === decodedUserToken()?.user_id && ' (you)'}
            {record?.is_company_owner && (
              <Tooltip
                title='Super admin gives access to all Teams'
                placement='top'
                getPopupContainer={() => document.body}
              >
                <span className='settings-users-super-admin-icon'>
                  <SuperAdmin
                    color='var(--text-primary)'
                    width={16}
                    height={16}
                  />
                </span>
              </Tooltip>
            )}
          </span>
          <CustomShipmentText
            noText={'--'}
            text={
              record?.is_pending && record?.send_email
                ? `Invite sent`
                : record?.is_pending && !record?.send_email
                  ? 'User added without invite'
                  : record?.title
            }
            fontSize={12}
            fontWeight={400}
            color='var(--text-secondary)'
            marginBottom={0}
          />
        </>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => (
        <>
          <CustomParagraph
            text={text}
            noText='--'
            fontSize={12}
            fontWeight={400}
            color='var(--text-primary)'
            marginBottom={0}
          />
        </>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (text) => (
        <>
          <CustomParagraph
            text={text}
            noText='--'
            fontSize={12}
            fontWeight={400}
            color='var(--text-primary)'
            marginBottom={0}
          />
        </>
      ),
    },
    {
      title: 'Last login',
      dataIndex: 'last_login',
      key: 'last_login',
      render: (text) => {
        const formatDate = (dateString) => formatDateTime(dateString) || '--';

        return (
          <>
            <CustomParagraph
              text={formatDate(text)}
              noText='--'
              fontSize={12}
              fontWeight={400}
              color='var(--text-primary)'
              marginBottom={0}
            />
          </>
        );
      },
    },

    {
      title: `${theme.themeType === 2 ? 'Client:Role' : 'Team:Role'}`,
      dataIndex: 'teams',
      key: 'teams',
      render: (_, record) => {
        const isDefaultAdmin = (team) =>
          String(team?.name || '').toLowerCase() === 'default' &&
          String(team?.role_name || '').toLowerCase() === 'admin';

        const teams = Array.isArray(record?.teams) ? record.teams : [];

        const defaultAdmin = teams.find(isDefaultAdmin);
        const primaryTeam = defaultAdmin ?? teams[0];
        const restTeams = defaultAdmin
          ? teams.filter((t) => t !== defaultAdmin)
          : teams.slice(1);

        return (
          <>
            {record?.is_active ? (
              teams.length > 0 ? (
                <div className='settings-users-teams'>
                  {primaryTeam && (
                    <TeamBadge
                      Truncate={false}
                      tooltipText={`${primaryTeam.name}: ${primaryTeam.role_name}`}
                      color={primaryTeam.color}
                      text={`${truncateByLength(primaryTeam.name, 20)}: ${truncateByLength(primaryTeam.role_name, 20)}`}
                    />
                  )}

                  {restTeams.length > 0 && (
                    <Tooltip
                      title={restTeams
                        .map((team) => `${team.name}: ${team.role_name}`)
                        .join(', ')}
                      placement='top'
                    >
                      <div className='settings-users-more-teams'>
                        <TeamBadge
                          color={null}
                          text={`+ ${restTeams.length}`}
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              ) : (
                <p>--</p>
              )
            ) : (
              <CustomParagraph
                text='Disabled'
                noText='--'
                fontSize={12}
                fontWeight={400}
                color='var(--text-primary)'
                marginBottom={0}
              />
            )}
          </>
        );
      },
    },

    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      width: 56,
      render: (text, record) => (
        <UserActionsMenu
          record={record}
          isCompanyAdmin={canSeeUserActivity}
          onEdit={handleEditUser}
          onResendInvite={handleResendInvite}
          onDisable={(rec) => handleDelete(rec, 1)}
          onDeletePending={(rec) => handleDelete(rec, 0)}
          onEnable={handleEdit}
          onActivity={handleOpenActivity}
          canViewAsUser={canViewAsUserBase && !record?.is_default_admin}
          onViewAsUser={(rec) => handleViewAsUser(rec, true)}
        />
      ),
    },
  ];

  // re-send invite
  const handleResendInvite = (record) => {
    if (record && record.id) {
      showSpin();
      ResendInvite(token, { user_id: record.id })
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data.data?.is_success) {
              message.success(data?.data?.message);
              fetchUsers(isChecked, page, search);
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    }
  };

  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  const TeamsFilteredColumns =
    allTeams?.length > 0
      ? columns
      : columns.filter((column) => column.key !== 'teams');

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          showToggle={true}
          toggleText={'Show disabled users'}
          title={`${title}`}
          totalCount={maxPageSize}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          addButtonWidth={'69px'}
          toggleChecked={isChecked}
          onToggleChange={handleToggle}
          addButtonText={'Invite'}
          onSearchChange={handleSearchChange}
          onSearchClear={handleSearchChange}
          showAddButton={UserPermissions?.is_create}
          onAddClick={() => {
            setShowInviteModal(true);
          }}
          extraActions={
            <UsersHeaderMenu
              isCompanyAdmin={canSeeUserActivity}
              onActivity={() => handleOpenActivity()}
            />
          }
        />
        <Table
          columns={TeamsFilteredColumns}
          dataSource={userList}
          pagination={false}
          className='settings-table settings-table-action-cols'
          rowHoverable={false}
          scroll={tableScroll}
        />
        {userList.length > 0 && (
          <Row style={{ width: '100%', marginTop: '12px' }} justify='center'>
            <Col>
              <CustomPagination
                maxPageSize={maxPageSize}
                page={page}
                totalPages={totalPages}
                pageSize={50}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </Col>
          </Row>
        )}
      </div>
      <InviteUserModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          fetchUsers(isChecked, page, search);
        }}
      />
      {showDrawer && (
        <CommonDrawer open={showDrawer} onClose={() => handleCloseDrawer()}>
          <SettingsInviteUserForm
            form={form}
            id={userID}
            handleCloseDrawer={handleCloseDrawer}
          />
        </CommonDrawer>
      )}

      <CustomDeleteModal
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleConfirmDelete()}
        title={
          deleteType === 0
            ? `Delete user`
            : deleteType === 1
              ? `Disable user`
              : `Enable user`
        }
        message={
          is_disable
            ? `User will lose access from Zoomlogi and be removed from all notifications.`
            : `Upon confirmation, the user will be granted access to Zoomlogi and will start receiving notifications.`
        }
        cancelText={'Cancel'}
        confirmText={
          deleteType === 0
            ? `Delete`
            : deleteType === 1
              ? `Disable user`
              : `Enable`
        }
        confirmColor={`#DE1135`}
      />
      {activityPanel.open && (
        <ActivityFeedPanel
          open={activityPanel.open}
          onClose={handleCloseActivity}
          initialUser={activityPanel.user}
          onEventClick={handleActivityEventClick}
          restoreState={activityPanel.restoreState}
        />
      )}
    </>
  );
};

export default SettingsUserTable;
